// Shared date-time formatter used by admin tables (CRM & WhatsApp, Bookings,
// Notes, etc.). Target format: "13 Oct 2026, 02:30 PM".
//
// All inputs are tolerant:
//   - ISO date-time:  "2026-04-21T14:12:25.478991+00:00"
//   - Date only:      "2026-04-21"
//   - Clock-time:     "14:30" / "2:30 PM" (combined with the date arg)
//
// If a time piece is missing or invalid we silently drop it and fall back to
// just the date, so legacy records keep rendering cleanly.

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const pad2 = (n) => String(n).padStart(2, "0");

const toDate = (val) => {
  if (!val) return null;
  if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
  const s = String(val).trim();
  if (!s) return null;
  // Date-only like "2026-04-21" — parse as local-date so the day doesn't
  // shift due to timezone (new Date("2026-04-21") is parsed as UTC midnight,
  // which can render as "20 Apr" in IST).
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

export const formatDate = (val) => {
  const d = toDate(val);
  if (!d) return "—";
  return `${pad2(d.getDate())} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

export const formatTime = (val) => {
  const d = toDate(val);
  if (!d) return "";
  const h24 = d.getHours();
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  const ampm = h24 >= 12 ? "PM" : "AM";
  return `${pad2(h12)}:${pad2(d.getMinutes())} ${ampm}`;
};

export const formatDateTime = (val) => {
  const d = toDate(val);
  if (!d) return "—";
  const date = `${pad2(d.getDate())} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
  // Date-only input (midnight local) → render date only.
  const s = typeof val === "string" ? val : "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return date;
  return `${date}, ${formatTime(d)}`;
};

/**
 * Normalises a customer-selected slot into a human-readable string for the
 * Admin → Bookings table. Three input shapes to support:
 *   1. Slot labels like "Morning (9 AM - 12 PM)" → compact "Morning · 9 AM–12 PM"
 *   2. 24-hour clock "14:30" / "14:30:00"        → "02:30 PM"
 *   3. Already-12-hour clock "2:30 PM"           → normalised "02:30 PM"
 * Empty / unparseable values return "" so the caller can render a dash.
 */
export const formatSetupTime = (val) => {
  const t = (val || "").toString().trim();
  if (!t) return "";

  // Slot label — "Morning (9 AM - 12 PM)" etc. We keep the human slot name
  // (first word) and shorten the range to fit a dense table column.
  const slotMatch = t.match(/^(morning|afternoon|evening|night)\s*\(([^)]+)\)\s*$/i);
  if (slotMatch) {
    const slot = slotMatch[1][0].toUpperCase() + slotMatch[1].slice(1).toLowerCase();
    const range = slotMatch[2].replace(/\s+/g, " ").replace(/\s*-\s*/g, "–").trim();
    return `${slot} · ${range}`;
  }

  // Standalone slot name without a range — just title-case it.
  if (/^(morning|afternoon|evening|night)$/i.test(t)) {
    return t[0].toUpperCase() + t.slice(1).toLowerCase();
  }

  // Already formatted (contains AM/PM) — just normalise spacing/case.
  const ampmMatch = t.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
  if (ampmMatch) {
    const h = parseInt(ampmMatch[1], 10);
    const m = parseInt(ampmMatch[2], 10);
    const suffix = ampmMatch[3].toUpperCase();
    if (isNaN(h) || isNaN(m)) return "";
    return `${pad2(h)}:${pad2(m)} ${suffix}`;
  }
  // 24-hour "HH:MM" or "HH:MM:SS".
  const hhmm = t.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (hhmm) {
    const h24 = parseInt(hhmm[1], 10);
    const m = parseInt(hhmm[2], 10);
    if (isNaN(h24) || isNaN(m) || h24 < 0 || h24 > 23 || m < 0 || m > 59) return "";
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    const ampm = h24 >= 12 ? "PM" : "AM";
    return `${pad2(h12)}:${pad2(m)} ${ampm}`;
  }
  return t;  // Last resort — show the raw value so admins see something.
};

// Combines a date ("2026-04-21") with a separate clock-time string
// ("14:30" or "2:30 PM") into the full "13 Oct 2026, 02:30 PM" form.
// Falls back to date-only if the clock-time can't be parsed.
export const formatEventDateTime = (dateStr, timeStr) => {
  const date = toDate(dateStr);
  if (!date) return "—";
  const datePretty = `${pad2(date.getDate())} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
  const t = (timeStr || "").toString().trim();
  if (!t) return datePretty;
  // "14:30" or "14:30:00"
  let m = t.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  let h = null, mins = null;
  if (m) { h = parseInt(m[1], 10); mins = parseInt(m[2], 10); }
  else {
    // "2:30 PM" / "2 PM"
    m = t.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM|am|pm)$/);
    if (m) {
      h = parseInt(m[1], 10) % 12;
      mins = m[2] ? parseInt(m[2], 10) : 0;
      if (/pm/i.test(m[3])) h += 12;
    }
  }
  if (h === null || mins === null || isNaN(h) || isNaN(mins)) return datePretty;
  const h12 = h % 12 === 0 ? 12 : h % 12;
  const ampm = h >= 12 ? "PM" : "AM";
  return `${datePretty}, ${pad2(h12)}:${pad2(mins)} ${ampm}`;
};
